import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Card,
  CardContent,
  Avatar,
  Fade,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Lock as LockIcon,
  VpnKey as KeyIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { TableService } from '../services/table.service';
import { UserService } from '../services/user.service';
import { DataTable, TablePermission, PermissionType } from '../types/table.types';
import { User, Role } from '../types/auth.types';

const permissionSchema = yup.object({
  targetType: yup.string().required('Тип цели обязателен'),
  targetId: yup.string().required('Цель обязательна'),
  permissions: yup.array().of(yup.string()).min(1, 'Выберите хотя бы одно разрешение'),
}).required();

enum TargetType {
  USER = 'user',
  ROLE = 'role',
}

interface PermissionFormData {
  targetType: TargetType;
  targetId: string;
  permissions: PermissionType[];
}

export default function TablePermissions() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [table, setTable] = useState<DataTable | null>(null);
  const [permissions, setPermissions] = useState<TablePermission[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [permissionDialog, setPermissionDialog] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState<{ id: string, name: string } | null>(null);
  const [mounted, setMounted] = useState(false);

  const { control, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<PermissionFormData>({
    resolver: yupResolver(permissionSchema) as Resolver<PermissionFormData>,
    defaultValues: {
      targetType: TargetType.USER,
      targetId: '',
      permissions: [],
    }
  });

  const targetType = watch('targetType');

  useEffect(() => {
    setMounted(true);
    if (id) {
      fetchData(id);
    }
  }, [id]);

  const fetchData = async (tableId: string) => {
    try {
      setLoading(true);

      const [tableData, permissionsData, usersData, rolesData] = await Promise.all([
        TableService.getTableById(tableId),
        TableService.getTablePermissions(tableId),
        UserService.getAllUsers(),
        UserService.getAllRoles(),
      ]);

      setTable(tableData);
      setPermissions(permissionsData);
      setUsers(usersData);
      setRoles(rolesData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPermissionDialog = () => {
    reset({
      targetType: TargetType.USER,
      targetId: '',
      permissions: [],
    });
    setPermissionDialog(true);
  };

  const handleClosePermissionDialog = () => {
    setPermissionDialog(false);
  };

  const handleAddPermission = async (data: PermissionFormData) => {
    if (!id) return;

    try {
      setSubmitting(true);

      const permissionData = {
        tableId: id,
        userId: data.targetType === TargetType.USER ? data.targetId : undefined,
        roleId: data.targetType === TargetType.ROLE ? data.targetId : undefined,
        permissions: data.permissions,
      };

      await TableService.setTablePermission(id, permissionData);

      // Перезагружаем пермишны с сервера (с populated role/user)
      const permissionsData = await TableService.getTablePermissions(id);
      setPermissions(permissionsData);

      setSuccess('Разрешение успешно добавлено');
      setPermissionDialog(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка добавления разрешения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = (permission: TablePermission) => {
    let name = 'Неизвестно';

    if (permission.user) {
      name = `Пользователь: ${permission.user.fullName}`;
    } else if (permission.role) {
      name = `Роль: ${permission.role.name}`;
    }

    setPermissionToDelete({
      id: permission.id,
      name,
    });
    setConfirmDeleteOpen(true);
  };

  const handleRemovePermission = async () => {
    if (!id || !permissionToDelete) return;

    try {
      await TableService.removeTablePermission(id, permissionToDelete.id);

      setPermissions(permissions.filter(p => p.id !== permissionToDelete.id));

      setSuccess('Разрешение успешно удалено');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка удаления разрешения');
    } finally {
      setConfirmDeleteOpen(false);
      setPermissionToDelete(null);
    }
  };

  const handleCancel = () => {
    navigate(`/tables/${id}`);
  };

  const getPermissionTypeName = (type: PermissionType): string => {
    switch (type) {
      case PermissionType.READ:
        return 'Чтение';
      case PermissionType.WRITE:
        return 'Запись';
      case PermissionType.DELETE:
        return 'Удаление';
      case PermissionType.ADMIN:
        return 'Администрирование';
      default:
        return type;
    }
  };

  const getPermissionColor = (type: PermissionType) => {
    switch (type) {
      case PermissionType.READ:
        return '#4facfe';
      case PermissionType.WRITE:
        return '#f093fb';
      case PermissionType.DELETE:
        return '#f44336';
      case PermissionType.ADMIN:
        return '#ff9800';
      default:
        return '#757575';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (!table) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          Таблица не найдена или у вас нет доступа к ней
        </Typography>
        <Button variant="contained" onClick={() => navigate('/tables')} sx={{ mt: 2 }}>
          Вернуться к списку таблиц
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Gradient */}
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: 3,
            p: 4,
            mb: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <SecurityIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Управление доступом
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                  Таблица: {table.name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>

      {error && (
        <Fade in timeout={400}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        </Fade>
      )}

      {success && (
        <Fade in timeout={400}>
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            {success}
          </Alert>
        </Fade>
      )}

      {/* Permissions Card */}
      <Fade in={mounted} timeout={600}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'all 0.3s',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              p: 2.5,
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ShieldIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Текущие разрешения
                </Typography>
                <Chip
                  label={permissions.length}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenPermissionDialog}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                Добавить разрешение
              </Button>
            </Box>
          </Box>

          <CardContent sx={{ p: 3 }}>
            {permissions.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <LockIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Нет настроенных разрешений
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Добавьте разрешения для управления доступом к таблице
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {permissions.map((permission, index) => (
                  <ListItem
                    key={permission.id}
                    divider={index < permissions.length - 1}
                    sx={{
                      py: 2.5,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(240, 147, 251, 0.05)',
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: permission.user ? 'rgba(33, 150, 243, 0.1)' : 'rgba(156, 39, 176, 0.1)',
                        color: permission.user ? '#2196f3' : '#9c27b0',
                        mr: 2,
                        width: 48,
                        height: 48
                      }}
                    >
                      {permission.user ? <PersonIcon /> : <GroupIcon />}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {permission.user
                            ? `Пользователь: ${permission.user.fullName}`
                            : permission.role
                              ? `Роль: ${permission.role.name}`
                              : 'Неизвестно'}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {permission.permissions.map((perm) => (
                            <Chip
                              key={perm}
                              label={getPermissionTypeName(perm)}
                              size="small"
                              sx={{
                                bgcolor: `${getPermissionColor(perm)}20`,
                                color: getPermissionColor(perm),
                                fontWeight: 600,
                                border: `1px solid ${getPermissionColor(perm)}40`
                              }}
                            />
                          ))}
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleConfirmDelete(permission)}
                        color="error"
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(244, 67, 54, 0.08)',
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Fade>

      {/* Action Buttons */}
      <Fade in={mounted} timeout={800}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              borderColor: '#f5576c',
              color: '#f5576c',
              '&:hover': {
                borderColor: '#f5576c',
                bgcolor: 'rgba(245, 87, 108, 0.08)',
              }
            }}
          >
            Вернуться к таблице
          </Button>
        </Box>
      </Fade>

      {/* Add Permission Dialog */}
      <Dialog
        open={permissionDialog}
        onClose={handleClosePermissionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SecurityIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Добавление разрешения
            </Typography>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(handleAddPermission)}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Controller
                name="targetType"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Тип цели"
                    fullWidth
                    margin="normal"
                    error={!!errors.targetType}
                    helperText={errors.targetType?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#f5576c',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f5576c',
                        }
                      }
                    }}
                  >
                    <MenuItem value={TargetType.USER}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" />
                        Пользователь
                      </Box>
                    </MenuItem>
                    <MenuItem value={TargetType.ROLE}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupIcon fontSize="small" />
                        Роль
                      </Box>
                    </MenuItem>
                  </TextField>
                )}
              />

              <Controller
                name="targetId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={targetType === TargetType.USER ? 'Пользователь' : 'Роль'}
                    fullWidth
                    margin="normal"
                    error={!!errors.targetId}
                    helperText={errors.targetId?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#f5576c',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f5576c',
                        }
                      }
                    }}
                  >
                    {targetType === TargetType.USER ? (
                      users.map(user => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.fullName} ({user.username})
                        </MenuItem>
                      ))
                    ) : (
                      roles.map(role => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name} - {role.description}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                )}
              />

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1.5, fontWeight: 600 }}>
                Разрешения:
              </Typography>

              <FormGroup>
                <Controller
                  name="permissions"
                  control={control}
                  render={({ field }) => (
                    <Box sx={{
                      p: 2,
                      bgcolor: 'rgba(240, 147, 251, 0.05)',
                      borderRadius: 2,
                      border: '1px solid rgba(240, 147, 251, 0.2)'
                    }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value?.includes(PermissionType.READ)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                field.onChange([...field.value, PermissionType.READ]);
                              } else {
                                field.onChange(field.value.filter(p => p !== PermissionType.READ));
                              }
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <KeyIcon fontSize="small" sx={{ color: '#4facfe' }} />
                            Чтение
                          </Box>
                        }
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value?.includes(PermissionType.WRITE)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                field.onChange([...field.value, PermissionType.WRITE]);
                              } else {
                                field.onChange(field.value.filter(p => p !== PermissionType.WRITE));
                              }
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <KeyIcon fontSize="small" sx={{ color: '#f093fb' }} />
                            Запись
                          </Box>
                        }
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value?.includes(PermissionType.DELETE)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                field.onChange([...field.value, PermissionType.DELETE]);
                              } else {
                                field.onChange(field.value.filter(p => p !== PermissionType.DELETE));
                              }
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <KeyIcon fontSize="small" sx={{ color: '#f44336' }} />
                            Удаление
                          </Box>
                        }
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value?.includes(PermissionType.ADMIN)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                field.onChange([...field.value, PermissionType.ADMIN]);
                              } else {
                                field.onChange(field.value.filter(p => p !== PermissionType.ADMIN));
                              }
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <KeyIcon fontSize="small" sx={{ color: '#ff9800' }} />
                            Администрирование
                          </Box>
                        }
                      />
                    </Box>
                  )}
                />
              </FormGroup>

              {errors.permissions && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {errors.permissions.message}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleClosePermissionDialog}>
              Отмена
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <SecurityIcon />}
              disabled={submitting}
              sx={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)',
                }
              }}
            >
              {submitting ? 'Сохранение...' : 'Добавить разрешение'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Удаление разрешения"
        message={`Вы уверены, что хотите удалить разрешение для "${permissionToDelete?.name}"?`}
        confirmText="Удалить"
        cancelText="Отмена"
        onConfirm={handleRemovePermission}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setPermissionToDelete(null);
        }}
        severity="warning"
      />
    </Box>
  );
}